// frontend/src/utils/pageViewBeacon.js
//
// Pure decision helper for the marketing-attribution page-view beacon (#100).
//
// Extracted from AnalyticsContext so the debounce rule is unit-testable without
// standing up a router. The rule: fire once per path, but suppress a repeat of
// the SAME path that arrives within `windowMs` (default 10s). Query-only route
// changes (e.g. a /blog ?sort= tweak) produce a new location with an unchanged
// pathname — this is exactly the case the window guards against.

export const PAGE_VIEW_DEBOUNCE_MS = 10000;

/**
 * Decide whether a page-view beacon should fire for `path`.
 *
 * @param {{path: string, ts: number}|null} last - Previous fired beacon state.
 * @param {string} path - Current pathname.
 * @param {number} now - Current epoch ms (Date.now()).
 * @param {number} [windowMs] - Debounce window in ms.
 * @returns {{fire: boolean, next: {path: string, ts: number}|null}}
 *          `fire` = send the beacon; `next` = the state to store for next time.
 */
export function decidePageViewBeacon(last, path, now, windowMs = PAGE_VIEW_DEBOUNCE_MS) {
  if (!path) return { fire: false, next: last };

  if (last && last.path === path && now - last.ts < windowMs) {
    // Same path within the window — dedupe, keep the earlier timestamp.
    return { fire: false, next: last };
  }

  return { fire: true, next: { path, ts: now } };
}

/**
 * Parse a bare hostname from a referrer URL, for the FIRST beacon of a session
 * only. Returns undefined for same-origin (in-app) referrers, empty referrers,
 * or anything unparseable — the payload field is optional and fail-quiet.
 *
 * @param {string} referrer - document.referrer.
 * @param {string} currentHost - window.location.hostname.
 * @returns {string|undefined} hostname (<=100 chars) or undefined.
 */
export function referrerDomain(referrer, currentHost) {
  if (!referrer) return undefined;
  try {
    const host = new URL(referrer).hostname;
    if (!host || host === currentHost) return undefined;
    return host.slice(0, 100);
  } catch (e) {
    return undefined;
  }
}
